/**
 * Mentions in the post composer: "@" and a few letters open a list of people (your connections first; only they
 * are notified), then public consortia and organizations (linked, not notified). Picked names stay plain "@Name"
 * while writing and become [@Name](/profile|consortium|organization/<id>/) when posted.
 * Backend: GET /interact/posts/mention-candidates/?q= ; notifications in interact/post/mentions.py.
 */
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Avatar, Box, Link, Paper, Typography } from '@mui/material';
import { Business as OrganizationIcon, Groups as ConsortiumIcon } from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';

/** The "@query" being typed just before the caret, as {query, start}, or null. */
export function mentionAt(text = '', caret = text.length) {
  const m = /(^|\s)@([\p{L}\p{N}_.-]{0,30})$/u.exec(text.slice(0, caret));
  return m ? { query: m[2], start: caret - m[2].length - 1 } : null;
}

// a mention link: a person, a public consortium or an organization
const MENTION_LINK = /\[@([^\]\n]{1,100})\]\(\/(profile|consortium|organization)\/([0-9a-fA-F-]{32,36})\/?\)/g;
const KIND_OF_PATH = { profile: 'person', consortium: 'consortium', organization: 'organization' };
const PATH_OF_KIND = { person: 'profile', consortium: 'consortium', organization: 'organization' };

/** The people, consortia and organizations a post's text mentions, as [{id, name, kind}] (to edit it as plain "@Name"). */
export const mentionsIn = (text = '') => [...text.matchAll(MENTION_LINK)]
  .map((m) => ({ name: m[1], id: m[3], kind: KIND_OF_PATH[m[2]] }));

/** The text with its mention links shown as the plain "@Name" the composer writes. */
export const plainMentions = (text = '') => text.replace(MENTION_LINK, '@$1');

const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** The text with each picked person's plain "@Name" turned into a mention link (names already linked stay). */
export function applyMentions(text = '', people = []) {
  return people.reduce((out, p) => out.replace(new RegExp(`(?<!\\[)@${escape(p.name)}(?![\\p{L}\\p{N}_])(?!\\]\\()`, 'gu'),
    `[@${p.name}](/${PATH_OF_KIND[p.kind || 'person']}/${p.id}/)`), text);
}

export function MentionList({ people, active, onPick }) {
  return (
    <Paper elevation={4} role="listbox" aria-label="People to mention"
      sx={{ mt: 0.5, maxHeight: 280, overflowY: 'auto', borderRadius: 2, zIndex: 5 }}>
      {people.map((p, i) => (
        <Box key={p.id} role="option" aria-selected={i === active}
          onMouseDown={(e) => { e.preventDefault(); onPick(p); }}          // before the text box loses focus
          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 0.9, cursor: 'pointer',
            bgcolor: i === active ? 'action.selected' : 'transparent', '&:hover': { bgcolor: 'action.hover' } }}>
          <Avatar src={p.avatar || undefined} variant={p.kind && p.kind !== 'person' ? 'rounded' : 'circular'}
            sx={{ width: 28, height: 28, fontSize: 13, bgcolor: 'primary.avatar', color: '#fff' }}>
            {p.kind === 'consortium' ? <ConsortiumIcon sx={{ fontSize: 17 }} />
              : p.kind === 'organization' ? <OrganizationIcon sx={{ fontSize: 17 }} /> : (p.name || '?')[0]}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{p.name}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {p.kind === 'consortium' || p.kind === 'organization'
                ? [p.kind === 'consortium' ? 'Consortium' : 'Organization', p.headline !== 'Consortium' && p.headline !== 'Organization' && p.headline]
                  .filter(Boolean).join(' · ')
                : [p.headline, !p.connected && "not connected: won't be notified"].filter(Boolean).join(' · ') || `@${p.username}`}
            </Typography>
          </Box>
        </Box>
      ))}
    </Paper>
  );
}

/**
 * "@…" in a text box: the list of people to mention, picked with the mouse or ↑ ↓ Enter / Tab (Esc closes).
 * Picked names stay plain "@Name" in VALUE; APPLY(text) turns them into profile links when the text is sent.
 * Spread FIELD onto the TextField (onChange, onKeyDown, onBlur) and render LIST under it. INITIAL: people
 * already mentioned (editing a post). Used by the post composer, the post editor and "Repost with your thoughts".
 */
export function useMentions(value, setValue, inputRef, initial = []) {
  const [mention, setMention] = useState(null);         // {query, start} while typing a mention
  const [mentioned, setMentioned] = useState(initial);  // [{id, name}] picked so far
  const [index, setIndex] = useState(0);
  const [candidates, setCandidates] = useState([]);
  useEffect(() => {
    if (!mention) { setCandidates([]); return undefined; }
    const timer = setTimeout(() => {
      axiosService.get(`/interact/posts/mention-candidates/?q=${encodeURIComponent(mention.query)}`)
        .then((res) => { setCandidates(res.data || []); setIndex(0); })
        .catch(() => setCandidates([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [mention?.query]);     // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (person) => {
    const el = inputRef.current;
    const caret = el ? el.selectionStart : value.length;
    const insert = `@${person.name} `;
    setValue(value.slice(0, mention.start) + insert + value.slice(caret));
    setMentioned((list) => (list.some((m) => m.id === person.id) ? list
      : [...list, { id: person.id, name: person.name, kind: person.kind || 'person' }]));
    setMention(null);
    const at = mention.start + insert.length;
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(at, at); } });
  };

  const onKeyDown = (e) => {
    if (!mention || !candidates.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setIndex((i) => (i + 1) % candidates.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIndex((i) => (i - 1 + candidates.length) % candidates.length); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(candidates[index]); }
    else if (e.key === 'Escape') { e.preventDefault(); setMention(null); }
  };

  return {
    field: {
      onChange: (e) => { setValue(e.target.value); setMention(mentionAt(e.target.value, e.target.selectionStart)); },
      onKeyDown,
      onBlur: () => setTimeout(() => setMention(null), 150),
    },
    list: mention && candidates.length > 0 ? <MentionList people={candidates} active={index} onPick={pick} /> : null,
    apply: (text) => applyMentions(text, mentioned),
    reset: (people = []) => { setMentioned(people); setMention(null); },
  };
}

/** Plain text (a reply, a comment) with its mentions as links to the person's, consortium's or organization's page;
 *  the rest stays exactly as written. */
export function MentionText({ text = '' }) {
  const parts = [];
  let last = 0;
  for (const m of text.matchAll(MENTION_LINK)) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <Link key={m.index} component={RouterLink} to={`/${m[2]}/${m[3]}/`} underline="hover" className="mention"
        onClick={(e) => e.stopPropagation()} sx={{ fontWeight: 600, color: 'primary.main' }}>
        @{m[1]}
      </Link>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}
